/**
 * Announcement Detail Screen
 * 
 * Full details view for a single announcement with attachment download
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  ArrowLeft,
  Download,
  Calendar,
  User,
  Megaphone,
  FileText,
  ExternalLink,
  AlertCircle
} from 'lucide-react';
import { AnnouncementService } from '../../services/AnnouncementService';
import { useAuth } from '../../contexts/AuthContext';
import { Announcement } from '../../@types/announcement';
import { AnnouncementPriorityBadge } from '../../components/announcements/AnnouncementPriorityBadge';
import { ThemeColors, Spacing, Typography, BorderRadius, Shadows } from '../../styles/ThemeStyles';
import { PageContainer, MainContent } from '../../styles/StyledComponents';

const Header = styled.div`
  margin-bottom: ${Spacing.xl};
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.sm} ${Spacing.md};
  border: 1px solid ${ThemeColors.border.main};
  background: white;
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  color: ${ThemeColors.text.primary};
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: ${Spacing.lg};

  &:hover {
    background: ${ThemeColors.background.secondary};
    border-color: ${ThemeColors.primary.main};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const Hero = styled.div<{ priority: string }>`
  width: 100%;
  height: 200px;
  background: ${props => {
    switch (props.priority) {
      case 'CRITICAL': return `linear-gradient(135deg, #DC2626 0%, #991B1B 100%)`;
      case 'HIGH': return `linear-gradient(135deg, #EA580C 0%, #C2410C 100%)`;
      case 'MEDIUM': return `linear-gradient(135deg, #F59E0B 0%, #D97706 100%)`;
      case 'LOW': return `linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)`;
      default: return `linear-gradient(135deg, ${ThemeColors.primary.main}, ${ThemeColors.primary.dark})`;
    }
  }};
  border-radius: ${BorderRadius.xl};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  margin-bottom: ${Spacing.xl};
  box-shadow: ${Shadows.xl};

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 50% 50%, rgba(255,255,255,0.2) 0%, transparent 70%);
  }
`;

const HeroIcon = styled.div`
  width: 100px;
  height: 100px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  position: relative;
  z-index: 1;

  svg {
    width: 50px;
    height: 50px;
  }
`;

const ContentSection = styled.div`
  background: white;
  border-radius: ${BorderRadius.xl};
  padding: ${Spacing.xxl};
  box-shadow: ${Shadows.md};
  margin-bottom: ${Spacing.xl};
`;

const Title = styled.h1`
  font-size: ${Typography.fontSize.xxxl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin: 0 0 ${Spacing.md} 0;
  line-height: 1.2;
  display: flex;
  align-items: center;
  gap: ${Spacing.md};
`;

const MetaInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing.lg};
  margin-bottom: ${Spacing.xl};
  padding-bottom: ${Spacing.xl};
  border-bottom: 1px solid ${ThemeColors.border.light};
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};

  svg {
    width: 18px;
    height: 18px;
    color: ${ThemeColors.primary.main};
  }

  strong {
    color: ${ThemeColors.text.primary};
    font-weight: ${Typography.fontWeight.semiBold};
  }
`;

const Content = styled.div`
  font-size: ${Typography.fontSize.md};
  line-height: 1.7;
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.xl};
  white-space: pre-wrap;
`;

const TextContent = styled.div`
  background: ${ThemeColors.background.secondary};
  padding: ${Spacing.xl};
  border-radius: ${BorderRadius.lg};
  border-left: 4px solid ${ThemeColors.primary.main};
  font-size: ${Typography.fontSize.md};
  line-height: 1.8;
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.xl};
  white-space: pre-wrap;
`;

const FilesSection = styled.div`
  margin-bottom: ${Spacing.xl};
`;

const SectionTitle = styled.h3`
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin: 0 0 ${Spacing.lg} 0;
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
`;

const FileCard = styled.a`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${Spacing.lg};
  background: white;
  border: 2px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.lg};
  margin-bottom: ${Spacing.md};
  cursor: pointer;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${ThemeColors.primary.main};
    background: ${ThemeColors.primary.light};
    transform: translateX(4px);
  }
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.md};
`;

const FileIconWrapper = styled.div`
  width: 48px;
  height: 48px;
  background: ${ThemeColors.primary.light};
  color: ${ThemeColors.primary.main};
  border-radius: ${BorderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 24px;
    height: 24px;
  }
`;

const FileDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const FileName = styled.div`
  font-size: ${Typography.fontSize.md};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.xs};
`;

const DownloadButton = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.sm} ${Spacing.md};
  background: ${ThemeColors.primary.main};
  color: white;
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};

  svg {
    width: 16px;
    height: 16px;
  }
`;

const LinkButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.sm};
  padding: ${Spacing.md} ${Spacing.xl};
  background: linear-gradient(135deg, ${ThemeColors.success.main}, ${ThemeColors.success.dark});
  color: white;
  border-radius: ${BorderRadius.lg};
  font-size: ${Typography.fontSize.md};
  font-weight: ${Typography.fontWeight.semiBold};
  text-decoration: none;
  transition: all 0.2s ease;
  box-shadow: ${Shadows.md};
  margin-top: ${Spacing.md};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${Shadows.lg};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${Spacing.xxl};
  font-size: ${Typography.fontSize.lg};
  color: ${ThemeColors.text.secondary};
`;

const ErrorState = styled.div`
  padding: ${Spacing.xl};
  background: ${ThemeColors.error.light};
  color: ${ThemeColors.error.dark};
  border-radius: ${BorderRadius.lg};
  text-align: center;
`;

const formatDate = (timestamp: any): string => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const AnnouncementDetailScreen: React.FC = () => {
  const { announcementId } = useParams<{ announcementId: string }>();
  const navigate = useNavigate();
  const { user, userRole } = useAuth();
  const { t } = useTranslation();
  
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Determine the correct announcements list path based on user role
  const announcementsPath = userRole === 'manager' ? '/manager/announcements' : '/announcements';

  useEffect(() => {
    const fetchAnnouncement = async () => {
      if (!announcementId) {
        setError('Announcement ID is required');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const data = await AnnouncementService.getAnnouncementById(announcementId);
        
        if (!data) {
          setError('Announcement not found');
          setIsLoading(false);
          return;
        }

        setAnnouncement(data);

        // Record view
        if (user?.id) {
          await AnnouncementService.recordView(announcementId, user.id);
        }
      } catch (err: any) {
        console.error('Error fetching announcement:', err);
        setError(err.message || 'Failed to load announcement');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAnnouncement();
  }, [announcementId, user]);

  if (isLoading) {
    return (
      <PageContainer isMenuCollapsed={false}>
        <MainContent>
          <LoadingState>Loading announcement...</LoadingState>
        </MainContent>
      </PageContainer>
    );
  }

  if (error || !announcement) {
    return (
      <PageContainer isMenuCollapsed={false}>
        <MainContent>
          <ErrorState>
            <AlertCircle style={{ marginBottom: Spacing.md, width: 48, height: 48 }} />
            <h2>Error</h2>
            <p>{error || 'Announcement not found'}</p>
            <BackButton onClick={() => navigate(announcementsPath)}>
              <ArrowLeft />
              Back to Announcements
            </BackButton>
          </ErrorState>
        </MainContent>
      </PageContainer>
    );
  }

  return (
    <PageContainer isMenuCollapsed={false}>
      <MainContent>
        <Header>
          <BackButton onClick={() => navigate(announcementsPath)}>
            <ArrowLeft />
            Back to Announcements
          </BackButton>
        </Header>

        <Hero priority={announcement.priority}>
          <HeroIcon>
            <Megaphone />
          </HeroIcon>
        </Hero>

        <ContentSection>
          <Title>
            {announcement.title}
            <AnnouncementPriorityBadge priority={announcement.priority} size="md" />
          </Title>

          <MetaInfo>
            <MetaItem>
              <Calendar />
              <span><strong>Created:</strong> {formatDate(announcement.createdAt)}</span>
            </MetaItem>
            {announcement.createdByName && (
              <MetaItem>
                <User />
                <span><strong>By:</strong> {announcement.createdByName}</span>
              </MetaItem>
            )}
            {announcement.expiresAt && (
              <MetaItem>
                <AlertCircle />
                <span><strong>Expires:</strong> {formatDate(announcement.expiresAt)}</span>
              </MetaItem>
            )}
          </MetaInfo>

          <Content>{announcement.content}</Content>

          {announcement.detailsLink && (
            <LinkButton href={announcement.detailsLink} target="_blank" rel="noopener noreferrer">
              <ExternalLink />
              View Details
            </LinkButton>
          )}

          {announcement.attachmentUrl && (
            <FilesSection>
              <SectionTitle>
                <FileText />
                Attachment
              </SectionTitle>
              <FileCard href={announcement.attachmentUrl} download target="_blank" rel="noopener noreferrer">
                <FileInfo>
                  <FileIconWrapper>
                    <FileText />
                  </FileIconWrapper>
                  <FileDetails>
                    <FileName>{announcement.attachmentName || 'Attachment'}</FileName>
                  </FileDetails>
                </FileInfo>
                <DownloadButton>
                  <Download />
                  Download
                </DownloadButton>
              </FileCard>
            </FilesSection>
          )}
        </ContentSection>
      </MainContent>
    </PageContainer>
  );
};

